import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { TallyDialog } from '@/components/TallyDialog';
import { 
  ClipboardList, 
  FileText, 
  Send, 
  Shield, 
  Clock, 
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Hammer,
  Zap,
  Target,
  Users,
  DollarSign,
  Calendar,
  Smartphone,
  Youtube,
  Linkedin,
  Facebook,
  Twitter,
  Instagram,
  Mail,
  Music
} from 'lucide-react';

const Index = () => {
  const [tallyOpen, setTallyOpen] = useState(false);

  useSeoMeta({
    title: 'JobWalk AI - Turn Job Walks Into Winning Proposals',
    description: 'AI-powered sales systems built specifically for mechanical, electrical, and plumbing contractors. Generate contract-grade scopes, automate follow-up, and protect your margin.',
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="JobWalk AI" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" asChild>
              <a href="#features">Features</a>
            </Button>
            <Button variant="ghost" asChild>
              <a href="#pricing">Pricing</a>
            </Button>
            <Button variant="ghost" asChild>
              <a href="/app">
                <Smartphone className="h-4 w-4 mr-2" />
                Mobile App
              </a>
            </Button>
            <Button onClick={() => setTallyOpen(true)}>
              Schedule Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.primary/10),transparent)]" />
        <div className="absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] bg-background shadow-xl shadow-primary/10 ring-1 ring-border sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center" />
        
        <div className="container px-6 py-24 sm:py-32 lg:py-40">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="mb-6 text-base px-4 py-1.5" variant="secondary">
              <Zap className="h-4 w-4 mr-2 inline-block text-accent" />
              Built for MEP Contractors
            </Badge>
            
            <h1 className="text-5xl font-bold tracking-tight sm:text-7xl bg-clip-text text-transparent bg-gradient-to-r from-foreground via-foreground to-foreground/70 mb-6">
              Turn Job Walks Into Winning Proposals
            </h1>
            
            <p className="text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto">
              AI-powered sales systems built specifically for mechanical, electrical, and plumbing contractors. Designed for how contractors actually sell — not generic AI tools.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg h-14 px-8 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all" onClick={() => setTallyOpen(true)}>
                  <Calendar className="mr-2 h-5 w-5" />
                  See a Real Proposal Demo
              </Button>
              <Button size="lg" variant="outline" className="text-lg h-14 px-8" asChild>
                <a href="#features">
                  Learn More
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Built by contractors to fix the gap between the job walk and the signed contract.
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-muted/30">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Good Contractors Lose Work for the Wrong Reasons
            </h2>
            <p className="text-lg text-muted-foreground">
              The problem isn't your team — it's outdated sales systems.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {[
              { icon: ClipboardList, title: "Lost Documentation", desc: "Job walk notes live in notebooks, phones, or someone's head" },
              { icon: Clock, title: "Slow Turnaround", desc: "Proposals take days (or weeks) to get out the door" },
              { icon: Send, title: "Inconsistent Follow-Up", desc: "Follow-up is inconsistent or forgotten altogether" },
              { icon: Users, title: "Bottlenecked Resources", desc: "Estimators are overloaded and pulled into sales support" },
              { icon: DollarSign, title: "Price Competition", desc: "Value gets lost, so price becomes the only differentiator" },
              { icon: Target, title: "Scope Ambiguity", desc: "Vague scopes lead to disputes and margin erosion later" },
            ].map((item, i) => (
              <Card key={i} className="border-2 hover:border-primary/20 transition-colors">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Estimator Bottleneck Section */}
      <section className="py-24">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-4">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              The Estimator Bottleneck
            </h2>
            <p className="text-lg text-muted-foreground">
              Most contractors don't lose work because they can't install it.<br />
              They lose work because their estimating capacity is limited.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3 max-w-6xl mx-auto mt-12">
            {/* Card 1 - The Reality */}
            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-primary" />
                </div>
                <Badge variant="outline" className="w-fit mb-2">The Reality</Badge>
                <CardTitle className="text-xl">Most estimating teams are overloaded.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">Estimators are expected to:</p>
                <ul className="space-y-2">
                  {[
                    "Attend job walks",
                    "Review drawings",
                    "Write scopes",
                    "Build pricing",
                    "Send proposals",
                    "Follow up with GCs"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground pt-2 border-t border-border">
                  When one estimator is handling everything, bids slow down and opportunities get missed.
                </p>
              </CardContent>
            </Card>

            {/* Card 2 - The Result */}
            <Card className="border-2 border-destructive/20 bg-destructive/5 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="h-14 w-14 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
                  <TrendingUp className="h-7 w-7 text-destructive" />
                </div>
                <Badge variant="outline" className="w-fit mb-2 border-destructive/30 text-destructive">The Result</Badge>
                <CardTitle className="text-xl">The sales pipeline becomes constrained by a few people.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">What this looks like:</p>
                <ul className="space-y-2">
                  {[
                    "Proposals take days or weeks",
                    "Inconsistent proposal quality",
                    "Missed follow-ups",
                    "Rushed scopes",
                    "Margin mistakes"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="text-destructive font-bold">✖</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground pt-2 border-t border-destructive/20">
                  The company may have the field capacity to grow — but the estimating process can't scale.
                </p>
              </CardContent>
            </Card>

            {/* Card 3 - The Fix */}
            <Card className="border-2 border-primary/40 bg-gradient-to-br from-card to-primary/5 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Zap className="h-7 w-7 text-primary" />
                </div>
                <Badge className="w-fit mb-2 bg-primary/10 text-primary border-primary/20">The Fix</Badge>
                <CardTitle className="text-xl">JobWalk AI removes the documentation burden from estimators.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">Instead of writing everything manually, the system:</p>
                <ul className="space-y-2">
                  {[
                    "Captures structured job walk notes",
                    "Generates clear scopes of work",
                    "Standardizes proposal language",
                    "Automates follow-up communication"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground pt-2 border-t border-primary/20">
                  Your estimators can focus on pricing and strategy instead of documentation.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Closing line */}
          <div className="max-w-3xl mx-auto mt-12 text-center space-y-6">
            <p className="text-lg font-semibold text-foreground">
              When estimators stop writing proposals from scratch, companies can bid more work without hiring more staff.
            </p>
            <Button size="lg" variant="outline" asChild>
              <a href="#features">
                See How a Job Walk Becomes a Proposal
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="py-24">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              How JobWalk AI Fixes the Real Problem
            </h2>
            <p className="text-lg text-muted-foreground">
              Proposals aren't the issue. The system behind them is.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-2 bg-gradient-to-br from-card via-card to-primary/5">
              <CardContent className="p-8 md:p-12">
                <div className="space-y-6 text-lg leading-relaxed">
                  <p className="text-foreground/90">
                    Most contractors don't lose work because they can't install it.
                  </p>
                  <p className="text-foreground/90">
                    They lose work — or margin — because everything between the job walk and the signed contract is messy, inconsistent, and dependent on a few people holding everything together.
                  </p>
                  <div className="pt-4 border-t border-border/50">
                    <p className="text-xl font-semibold text-primary">
                      JobWalk AI was built to fix that entire gap.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trusted By Contractors Micro-Section */}
      <section className="py-16 bg-primary">
        <div className="container px-6">
          <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-primary-foreground mb-3">
                Built for How Contractors Actually Sell
              </h2>
              <p className="text-primary-foreground/80 text-lg">
                JobWalk AI was designed around the real workflows of estimators and project managers in mechanical, electrical, plumbing, and low-voltage contracting.
              </p>
            </div>
            <div className="flex-1">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Electrical contractors",
                  "Mechanical contractors",
                  "Plumbing contractors",
                  "Structured cabling & low-voltage integrators",
                  "Design-build contractors"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 bg-primary-foreground/10 rounded-lg px-4 py-3">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <span className="text-primary-foreground font-medium text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-muted/30">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <Badge className="mb-4 text-base px-4 py-1.5" variant="outline">
              What JobWalk AI Does
            </Badge>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              More Than a Proposal Tool
            </h2>
            <p className="text-lg text-muted-foreground">
              JobWalk AI isn't just a proposal generator. It's a contractor-built system that lives between the job walk and the signed contract.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3 max-w-6xl mx-auto">
            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <FileText className="h-7 w-7 text-primary" />
                </div>
                <CardTitle className="text-2xl">Turn Job Walks Into Clear Proposals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {[
                    "Capture job walk notes in a structured format",
                    "Convert notes into clear, professional proposals",
                    "Standardize scope language across your team",
                    "Reduce proposal turnaround time"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="link" className="px-0 text-primary">
                  View a Contract-Grade Scope
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="h-14 w-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <Send className="h-7 w-7 text-accent" />
                </div>
                <CardTitle className="text-2xl">Automate Follow-Up Without Chasing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {[
                    "Automated, professional follow-up messaging",
                    "Consistent touchpoints after every bid",
                    "No CRM babysitting required",
                    "Keep your company top-of-mind with GCs"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="link" className="px-0 text-accent">
                  See How Follow-Up Is Systemized
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-primary" />
                </div>
                <CardTitle className="text-2xl">Protect Margin Without Cutting Price</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {[
                    "Clarify scope and assumptions upfront",
                    "Reduce rework caused by vague proposals",
                    "Frame value clearly for GCs and owners",
                    "Support better pricing conversations"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="link" className="px-0 text-primary">
                  See How We Protect 2–5% Margin
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Scope Comparison */}
      <section className="py-24">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Most Contractors Lose Margin in the Scope — Not the Field
            </h2>
            <p className="text-lg text-muted-foreground">
              JobWalk AI generates contract-grade Scopes of Work that are intentionally more detailed than industry norms — because ambiguity is expensive.
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-2">
            <Card className="border-2 border-destructive/20">
              <CardHeader>
                <CardTitle className="text-2xl text-destructive">What a Typical Scope Looks Like</CardTitle>
                <CardDescription>Vague, assumption-heavy, dispute-prone</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <div className="space-y-3">
                  <p className="flex items-start gap-3">
                    <span className="text-destructive mt-1">✗</span>
                    <span>"Provide labor and materials per drawings"</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Responsibility implied, not defined</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Existing conditions assumed</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Performance obligations unclear</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Disputes handled later</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/40 bg-gradient-to-br from-card to-primary/5">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">JobWalk AI Scopes</CardTitle>
                <CardDescription>Contract-grade, defensible, margin-protecting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <div className="space-y-3">
                  <p className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <span>Labor, materials, and responsibilities clearly separated</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <span>Assumptions documented upfront</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <span>Exclusions explicitly stated</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <span>Performance standards defined</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <span>Directional authority established before work starts</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="max-w-4xl mx-auto mt-12">
            <Card className="border-2">
              <CardContent className="p-8">
                <div className="text-center space-y-4">
                  <p className="text-lg font-semibold text-foreground">
                    Most proposals summarize work. JobWalk AI defines responsibility.
                  </p>
                  <p className="text-muted-foreground">
                    We build Scopes of Work the way experienced estimators and project managers actually think — clearly separating labor, materials, assumptions, exclusions, and directional authority before the job ever starts.
                  </p>
                  <p className="text-primary font-medium">
                    The result: fewer disputes, cleaner handoffs, and protected margin.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Before vs After Section */}
      <section className="py-24 bg-muted/30">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Before JobWalk AI vs. After JobWalk AI
            </h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-2 max-w-5xl mx-auto">
            {/* Before */}
            <Card className="border-2 border-destructive/30 bg-destructive/5">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-8 w-8 rounded-full bg-destructive/20 flex items-center justify-center text-destructive font-bold text-sm">✗</div>
                  <CardTitle className="text-2xl text-destructive">Before</CardTitle>
                </div>
                <CardDescription className="text-base font-medium text-foreground/70">
                  Job walk notes scattered across:
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-2">
                  {["Notebooks", "Phones", "Text messages", "Estimator memory"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-muted-foreground">
                      <span className="text-destructive font-bold">—</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Separator className="bg-destructive/20" />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Result:</p>
                  <ul className="space-y-2">
                    {[
                      "Slow proposals",
                      "Missed follow-ups",
                      "Unclear scopes",
                      "Price becomes the only differentiator"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-muted-foreground">
                        <span className="text-destructive font-bold text-lg leading-none">✖</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* After */}
            <Card className="border-2 border-primary/40 bg-gradient-to-br from-card to-primary/5">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">✓</div>
                  <CardTitle className="text-2xl text-primary">After</CardTitle>
                </div>
                <CardDescription className="text-base font-medium text-foreground/70">
                  JobWalk AI turns every job walk into:
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-2">
                  {[
                    "Structured notes",
                    "Clear scope of work",
                    "Professional proposal",
                    "Automated follow-up"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-foreground">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Separator className="bg-primary/20" />
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Result:</p>
                  <ul className="space-y-2">
                    {[
                      "Faster proposals",
                      "Cleaner handoffs",
                      "Fewer disputes",
                      "Protected margin"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-foreground">
                        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-muted/30">
        <div className="container px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Simple, Straightforward Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to turn job walks into winning proposals.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2 max-w-5xl mx-auto">
            {/* Core System */}
            <Card className="border-2 hover:border-primary/40 transition-all">
              <CardHeader>
                <Badge className="w-fit mb-2" variant="secondary">Core System</Badge>
                <CardTitle className="text-4xl">
                  $149
                  <span className="text-lg font-normal text-muted-foreground"> / month</span>
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  Best for owner-operators and small teams.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Includes:</p>
                  <ul className="space-y-3">
                    {[
                      "Job walk capture",
                      "Proposal scope generator",
                      "Follow-up workflows",
                      "Mobile app",
                      "Proposal formatting system"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button className="w-full" size="lg" onClick={() => setTallyOpen(true)}>
                  Get Core Access
                </Button>
              </CardContent>
            </Card>

            {/* Guided Setup */}
            <Card className="border-2 border-primary bg-gradient-to-br from-card to-primary/5 hover:shadow-xl transition-all relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-accent text-accent-foreground text-sm px-4 py-1 shadow-md">
                  Most Popular
                </Badge>
              </div>
              <CardHeader>
                <Badge className="w-fit mb-2 bg-primary text-primary-foreground">Guided Setup</Badge>
                <CardTitle className="text-4xl">
                  $2,000
                  <span className="text-lg font-normal text-muted-foreground"> one-time</span>
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  We install JobWalk AI into your sales process.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Includes:</p>
                  <ul className="space-y-3">
                    {[
                      "Core system access",
                      "Sales workflow configuration",
                      "Proposal template setup",
                      "Margin protection framework",
                      "Follow-up automation"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button className="w-full bg-primary hover:bg-primary/90" size="lg" onClick={() => setTallyOpen(true)}>
                  Schedule a Setup Call
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Most companies start with Guided Setup */}
          <div className="max-w-5xl mx-auto mt-6">
            <Card className="border-2 border-accent/30 bg-accent/5">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                  <TrendingUp className="h-5 w-5 text-accent" />
                </div>
                <p className="text-foreground font-medium">
                  Most companies start with Guided Setup — it gets your team running faster and ensures everything is configured correctly from day one.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo" className="py-24">
        <div className="container px-6">
          <Card className="max-w-4xl mx-auto border-2 bg-gradient-to-br from-primary/5 via-card to-accent/5">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
                Ready to Fix Your Sales System?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Schedule a 30-minute demo to see how JobWalk AI can help you turn job walks into winning proposals with contract-grade scopes, automated follow-up, and protected margins.
              </p>
              <Button size="lg" className="text-lg h-14 px-8 shadow-lg shadow-primary/20" onClick={() => setTallyOpen(true)}>
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Demo
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30">
        <div className="container px-6 py-12">
          <div className="grid gap-8 md:grid-cols-3">
            {/* Brand */}
            <div className="space-y-4">
              <img src="/logo.png" alt="JobWalk AI" className="h-10 w-auto" />
              <p className="text-sm text-muted-foreground">
                AI-powered proposal systems for mechanical, electrical, and plumbing contractors.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Quick Links</h3>
              <nav className="flex flex-col space-y-2">
                <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Features
                </a>
                <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Pricing
                </a>
                <a href="/app" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Mobile App
                </a>
                <button onClick={() => setTallyOpen(true)} className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left">
                  Schedule Demo
                </button>
              </nav>
            </div>

            {/* Connect */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Connect With Us</h3>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="icon" asChild>
                  <a href="https://www.youtube.com/@JobWalkAI" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                    <Youtube className="h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href="https://www.linkedin.com/company/jobwalkai" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <Linkedin className="h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href="https://www.facebook.com/JobWalkAI" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <Facebook className="h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href="https://twitter.com/JobWalkAI" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                    <Twitter className="h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href="https://www.instagram.com/jobwalkai" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href="https://www.tiktok.com/@jobwalkai" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <Music className="h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href="mailto:sales@jobwalkai.com" aria-label="Email">
                    <Mail className="h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <Separator className="my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 JobWalk AI. All rights reserved.
            </p>
            <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </footer>

      {/* Tally Form Dialog */}
      <TallyDialog open={tallyOpen} onOpenChange={setTallyOpen} />
    </div>
  );
};

export default Index;
